(ns dre.pages.frontpage.keyword-search.views
  (:require
   ["antd" :refer [Spin Popover]]
   [dre.app.events :as app-events]
   [dre.app.router :as router :refer [->route ->route-with-query-params]]
   [dre.components.searchbar.main-searchbar :refer [main-searchbar]]
   [dre.components.dropdown :refer [dropdown dropdown-list]]
   [dre.components.icon :refer [icon fa-icon svg]]
   [dre.i18n.views :refer [tr]]
   [dre.modals.events :as-alias modals]
   [dre.pages.my-searches.signals.events :as-alias signals-events]
   [dre.pages.my-searches.signals.views :refer [signal-detail]]
   [dre.pages.my-searches.search-history.views :refer [event-detail date->yyyy-mm-dd]]
   [dre.pages.my-searches.search-history.events :as search-history-events]
   [dre.pages.frontpage.keyword-search.events :as pages-keyword-search]
   [dre.utils.bem :as bem]
   [dre.utils :refer [roles-hash]]
   [re-frame.core :as rf]
   [reagent.core :as r]))

(def component-name :keyword-search)

(def example-searches
  [{:title "Monoclonal antibodies for Atopic Dermatitis or Asthma (2020 to date)"
    :query/blunt "x(ppmdescendants).k(SUyz.Monoclonal Antibody).x(l).kg(SUDUc.SUC1g.Atopic Dermatitis.).x(or).k(SUBAB.Asthma).x(r).p(2020-01-01.)"}
   {:title "Multiple Myeloma - Active or recruiting RCTs"
    :query/blunt "k(SUGiN.Multiple Myeloma).g(predicted/D016449.Randomized Control Trial).x(l).g(study/active.Active).x(or).g(study/recruiting.Recruiting).x(r)"}
   {:title "Atopic Dermatitis - RWE authors in Europe"
    :query/blunt "kg(SUDUc.SUC1g.Atopic Dermatitis.).g(predicted/rwe.Real-world Evidence).x(ppmdescendants).kg(S0B0i.SUDtJ.Europe.).x(stsaffiliation).x(l).p(2010-01-01.).x(and).p(.2020-12-31).x(r)"}
   {:title "Safety of FOLFOX/FOLFOXIRI regimens in gastric or colorectal cancers"
    :query/blunt "x(l).k(SURRG-.FOLFOXIRI).x(or).k(SUBf7w.FOLFOX Regimen).x(r).x(l).k(SUIzM.Safety).x(ststitle-or-abstract).x(or).k(SUaZ.Adverse Event).x(ststitle-or-abstract).x(r).x(l).x(ppmdescendants).k(SUCS7.Colorectal carcinoma).x(or).x(ppmdescendants).kg(SUGAw.SUCq2Q.Gastric Cancer.).x(r)"}
   {:title "Prevalence of COPD in the US"
    :query/blunt "kg(SUF42.SUFY2X.COPD.).x(ststitle).kg(SU17l.SUIFS.Prevalence.).x(ststitle).k(SUKLo.United States)"}])

(defn view
  []
  (let [searchbar-ref (r/atom nil)]
    (r/create-class
     {:component-did-mount
      (fn [this]
        (rf/dispatch [::pages-keyword-search/init-db])
        (rf/dispatch [::search-history-events/fetch])
        (.click @searchbar-ref))
      :reagent-render
      (fn []
        (let [user @(rf/subscribe [::app-events/user])
              r-hash (roles-hash user)
              limit 10
              signals @(rf/subscribe [::signals-events/signals])
              signals-loading? @(rf/subscribe [::signals-events/loading?])
              search-history @(rf/subscribe [::search-history-events/data])
              search-history-loading? @(rf/subscribe [::search-history-events/loading?])
              signals (cond->>
                       (concat
                     ;; Signals that have been visited, recently viewed first
                        (->> signals
                             (drop-while (comp nil? :signal/last-viewed))
                             (sort-by :signal/last-viewed)
                             reverse)
                      ;; Signals that haven't been visited, recently created first
                        (->> signals
                             (take-while (comp nil? :signal/last-viewed))
                             reverse))
                        (pos-int? limit)
                        (take limit))]
          [:div
           {:class component-name}
           [:div
            {:class (bem/be component-name :search-bar-div)}
            [main-searchbar {:db-root-path [:search]
                             :searchbar-ref searchbar-ref}
             {:show-filters? false}]
            [:div
             {:class (bem/be component-name :search-bar-menu)}
             [:span
              {:class :item}
              [dropdown
               {:anchor
                [:a
                 {:class :title}
                 [tr "Example Searches"]
                 [fa-icon :chevron-down]]
                :content
                [dropdown-list
                 (for [example example-searches]
                   ^{:key (hash example)}
                   [:a
                    {:href (->route-with-query-params (->route :search) {:query (:query/blunt example)})}
                    (:title example)])]}]]
             [:span
              {:class :item}
              [dropdown
               {:anchor
                [:a
                 {:class :title}
                 [tr "Search History"]
                 [fa-icon :chevron-down]]
                :content
                [dropdown-list
                 (if search-history-loading?
                   [[:> Spin]]
                   (if (not (seq search-history))
                     [[:span [tr "No data found"]]]
                     (for [search search-history]
                       ^{:key (hash search)}
                       [:> Popover
                        {:content (r/as-element (search-history-events/render-event (:payload search)))
                         :trigger :hover
                         :placement :top}
                        [:a
                         {:on-click (partial event-detail search r-hash)}
                         (:title search) " - " (date->yyyy-mm-dd (:_created search))]])))]}]]
             [:span
              {:class :item}
              [dropdown
               {:class (bem/be component-name :saved-searches-dropdown)
                :anchor
                [:a
                 {:class [:title (bem/be component-name :saved-searches-dropdown-anchor)]}
                 [tr "Saved Searches"]
                 [fa-icon :chevron-down]]
                :content
                [dropdown-list
                 (concat
                  (if signals-loading?
                    [[:> Spin]]
                    (if (not (seq signals))
                      [[:a
                        {:onClick #(rf/dispatch [::modals/set-modal :my-searches.signals/edit {:data-edit {}}])}
                        [tr "Create your first saved search"]]]
                      (for [signal signals]
                        ^{:key (hash signal)}
                        [:a
                         {:class (bem/be component-name :saved-searches-dropdown-item)
                          :on-click (partial signal-detail signal r-hash)}
                         (:signal/title signal)])))
                  [[:a.dropdown-footer
                    {:href (->route :signals)}
                    [icon :common :save]
                    [tr "All Saved Searches"]]])]}]]]]]))})))
